import { useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Drawer from "../../index";
import Button from "@components/Button";
import TabSelector from "@components/TabSelector";
import SectionHeader from "@components/SectionHeader";
import TickIcon from "../../img/tick-icon.svg?react";
import CloseIcon from "../../img/close-icon.svg?react";
import "./style.scss";
import {
  setMessagesAsClosed,
  setMessagesAsSeen,
} from "../../../../api/message";
import { useVisibleMessages } from "../../../../api/message/useVisibleMessages";
import {
  getNotificationTarget,
  openAppLink,
  parseDate,
  resolveCmsUrl,
} from "../../../../Helpers";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";

const EmptyList = ({ previous }) => {
  return (
    <div className="drawer-notifications-empty-list">
      <TickIcon />

      {previous ? (
        <p className="copy">
          Du hast keine neuen
          <br /> Benachrichtigungen!
        </p>
      ) : (
        <p className="copy">
          Du hast keine neuen
          <br /> Benachrichtigungen!
        </p>
      )}
    </div>
  );
};

const tabContent = ["Ungelesen", "Alle"];

export const NotificationItem = ({ title, date, desc, url, unseen, hide }) => {
  const navigate = useNavigate();
  const target = typeof url === "boolean" ? url : resolveCmsUrl(url);

  return (
    <div
      className={`drawer-notifications-item ${target ? "url" : ""} ${
        unseen ? "unseen" : ""
      }`}
      onClick={() => {
        if (!target) {
          return;
        }

        if (target === true) {
          hide?.();
          return;
        }

        openAppLink(target, navigate);
        hide?.();
      }}
    >
      <time className="date">{date}</time>
      <div className="top">
        <hr />
        <h5 className="title" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title)}} />
      </div>
      <p className="desc" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(desc)}} />
    </div>
  );
};

const NotificationsDrawer = NiceModal.create(() => {
  const modal = useModal();

  const [selectedTab, setSelectedTab] = useState(0);

  const {
    messages: unseenMessages,
    detailsQuery,
  } = useVisibleMessages();

  const {
    messages: seenMessages,
    listQuery: messageListSeenQuery,
  } = useVisibleMessages({ seen: true });

  const setAllAsSeen = () => {
    setMessagesAsSeen({}, () => {
      detailsQuery.refetch();
    });
  };

  const setAllAsClosed = () => {
    setMessagesAsClosed({}, () => {
      messageListSeenQuery.refetch();
      detailsQuery.refetch();
    });
  };

  return (
    <Drawer
      isOpen={modal.visible}
      onClose={() => {
        modal.hide();
        setAllAsSeen();
        detailsQuery.refetch();
      }}
      onAfterClose={() => modal.remove()}
      disableClose={modal?.args?.disableClose ?? true}
      innerClass={""}
      from="fromRight"
    >
      <div className="drawer-notifications">
        <div className="top">
          <div className="top-flex">
            <SectionHeader>Benachrichtigungen</SectionHeader>
            <button
              onClick={() => {
                modal.hide();
                setAllAsSeen();
                detailsQuery.refetch();
              }}
            >
              <CloseIcon />
            </button>
          </div>
          <TabSelector
            selectedTab={selectedTab}
            tabContent={tabContent}
            setSelectedTab={setSelectedTab}
          />
        </div>

        <div className="items">
          {selectedTab === 0 && (
            <>
              {unseenMessages.length > 0 ? (
                unseenMessages.map((notify, i) => (
                  <NotificationItem
                    key={i}
                    title={notify?.content?.title}
                    desc={notify?.content?.description}
                    date={parseDate(notify?.created)}
                    url={getNotificationTarget(notify)}
                    hide={() => {
                      modal.hide();
                    }}
                    unseen
                  />
                ))
              ) : (
                <EmptyList previous={false} />
              )}
            </>
          )}
          {selectedTab === 1 && (
            <>
              {seenMessages.length > 0 ? (
                seenMessages.map((notify, i) => (
                  <NotificationItem
                    key={i}
                    title={notify?.content?.title}
                    desc={notify?.content?.description}
                    date={parseDate(notify?.created)}
                    url={getNotificationTarget(notify)}
                    hide={() => {
                      modal.hide();
                    }}
                  />
                ))
              ) : (
                <EmptyList previous={true} />
              )}
            </>
          )}
        </div>

        {selectedTab === 1 && seenMessages.length > 0 && (
            <div className="bottom">
              <Button
                action={() => {
                  setAllAsClosed();
                }}
              >
                Alle entfernen
              </Button>
            </div>
          )}
      </div>
    </Drawer>
  );
});

NiceModal.register("drawer-notifications", NotificationsDrawer);

export default NotificationsDrawer;
